// ═══════════════════════════════════════════════════════════════════════════════════════════
// SendSearchTable — the main result grid (CONTRACT §4.2, `SendSearch-Mock-v3.html:200-212`).
//
// DELIVERY PATH: _delivery\SendSearch-V1\react\screens\SendSearch\components\SendSearchTable.tsx
// TARGET PATH:   ReactCode\src\screens\SendSearch\components\SendSearchTable.tsx
//
// COLUMN ORDER — 11-sendsearch §8 (design approved by Idan 2026-09-30), CHANGED 2026-10-02 (Idan):
//   נמען | אימייל | וואטסאפ | מפקח | ‹גרסה› | נשלח | [action]
// The שליחה column is gone: its campaign name now heads each channel column (the e-mail campaign
// in אימייל, the WhatsApp unit in וואטסאפ) and its data-source line sits at the bottom of נמען.
// One report row = one person in one SEND, so each channel has its OWN column (status + time, and
// for WhatsApp a grey sub-line) and `—` when the send has no such channel. They replace the old
// "ערוצים ותוצאה" + "הראיה האחרונה" pair, which read one cross-channel verdict out of two channels.
// The version column still sits IMMEDIATELY BEFORE נשלח — CONTRACT §4.2, non-negotiable: the
// version and the send time are one thought ("what was sent, when"), and separating them is what
// let a wrong-version send go unnoticed.
//
// A whole row is clickable (`:299` — "לחיצה על שורה פותחת פירוט"). A supervisor / roll-up recipient
// opens the ROLL-UP drawer; everyone else opens the AGENT drawer.
//
// NO PERCENTAGES anywhere and NO cross-channel matrix cell (§4.2 / `:443`).
// ═══════════════════════════════════════════════════════════════════════════════════════════

import React from 'react';
import {
    Box, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
    TableRow, Tooltip, Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { DateFormats } from '../../../helpers/Constants';
import InlineBanner from '../../SmartSend/components/InlineBanner';
import {
    SS,
    SendSearchRow,
    SendSearchCampaignSource,
    eSendSearchChannel,
    channelFilterKey,
    rowEmailAttempt,
    rowSendHasEmail,
    rowWhatsAppAttempt,
    rowRecipientEmailBlockOf,
    rowRecipientPhoneOf,
    waSubLineOf,
    sendSearchRowKey,
    PAGE_SIZE_OPTIONS,
} from '../../../Models/DataSources/SendSearch';
import ChannelStateCell from './ChannelStateCell';
import ContactBlockChip from './ContactBlockChip';
import VersionBadge from './VersionBadge';
import { sortValueDisplayOf } from './SendSearchAdvanced';

interface Props {
    items: SendSearchRow[];
    totalCount: number;
    pageIndex: number;      // 0-based, as the SP is
    pageSize: number;
    loading: boolean;
    hasFilter: boolean;
    onOpenRow: (row: SendSearchRow) => void;
    onPageChange: (pageIndex: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onClearAll: () => void;
    // ── the sort-value column (CONTRACT §2) ──────────────────────────────────────────────────
    // The DISPLAY NAME of the field the grid is currently sorted by, or null for the server's
    // default order. When set, one extra column appears showing `row.SortValueDisplay` — the raw
    // value the server actually sorted on.
    //
    // WHY THE COLUMN IS MANDATORY WHEN SORTING BY A HIDDEN FIELD: the user can sort by any
    // searchable column, and almost none of them are columns of this grid. Without this cell the
    // rows simply reorder for no visible reason — indistinguishable, on screen, from a paging bug —
    // and the user cannot check that the ordering is the one they asked for. §2 requires it on RS1,
    // MapRow, both row models and the channel≠1 stub for exactly this reason; this is its render.
    sortFieldLabel?: string | null;

    // ── data-source identity (52-SearchSends-SourceColumn.sql, result set 3) ─────────────────
    // The map for THIS result set. Rows carry `EffectiveDataSourceID`; the name is joined here.
    sources?: SendSearchCampaignSource[];
    // 🔴 THE GATE. false ⇒ the server has not shipped 52_ ⇒ render NO source line at all. It must
    // never degrade into a line reading "unknown source" on every row: that is a claim about the
    // DATA, made from a fact about the DEPLOYMENT.
    sourcesAvailable?: boolean;

    // ── 11-sendsearch §8 ──────────────────────────────────────────────────────────────────────
    // The load error, so an empty grid can say WHICH empty it is: "the search failed" is not "no
    // recipient matches", and painting the first as the second is a claim about the data made from
    // a fact about the network. 'PERMISSION_DENIED' is its own sentence (HideRecipietns).
    error?: string | null;
    // The APPLIED channel filter. Under אימייל / וואטסאפ the empty state names the channel and
    // offers the way back to both, because "no results" under a channel filter is a different
    // answer from "no results".
    channel?: eSendSearchChannel;
    onShowAllChannels?: () => void;
}

const fmt = (iso: string | null): string => (iso ? moment(iso).format(DateFormats.DATE_TIME_24) : '—');

// Screen-reader-only. Written out rather than imported: `visuallyHidden` is MUI v5, this is v4.
const SR_ONLY: React.CSSProperties = {
    position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
    overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};

const SendSearchTable: React.FC<Props> = ({
    items, totalCount, pageIndex, pageSize, loading, hasFilter,
    onOpenRow, onPageChange, onPageSizeChange, onClearAll, sortFieldLabel,
    sources, sourcesAvailable, error, channel, onShowAllChannels,
}) => {
    const { t, i18n } = useTranslation();
    // Same idiom as DataSources.tsx:113 — fallback 'rtl' because Hebrew is the default locale.
    const isRtl = (i18n.dir?.() ?? 'rtl') === 'rtl';

    // ── data-source line ─────────────────────────────────────────────────────────────────────
    // Built once per render, not per row: 50 rows × a linear scan would be 50 scans of the map.
    const srcList: SendSearchCampaignSource[] = sources ?? [];
    const srcById: { [id: number]: SendSearchCampaignSource } = {};
    srcList.forEach((s) => { srcById[s.DataSourceID] = s; });

    // Which NAMES are carried by more than one id. Case-folded and trimmed, because "תיק סוכן" and
    // "תיק סוכן " are the same name to a reader and the filtered unique index does not stop them
    // from coexisting once one side is deleted.
    //
    // This drives EMPHASIS only — never whether the id is shown. The id is unconditional: this map
    // covers the filtered result, so a search returning only the deleted twin contains no collision,
    // and a conditional id would vanish exactly where it is most needed while its absence read as a
    // positive claim of uniqueness.
    const nameCounts: { [name: string]: number } = {};
    srcList.forEach((s) => {
        const key = (s.DataSourceName ?? '').trim().toLowerCase();
        if (key === '') return;
        nameCounts[key] = (nameCounts[key] || 0) + 1;
    });

    const renderSourceLine = (r: SendSearchRow) => {
        // The gate. Absent flag ⇒ the SERVER cannot tell us ⇒ no line at all. Never a line that
        // asserts something about the data from a fact about the deployment.
        if (!sourcesAvailable) return null;

        const id = r.EffectiveDataSourceID;
        const src = id != null ? srcById[id] : undefined;
        const name = src ? src.DataSourceName : null;
        const isDeleted = !!(src && src.IsDeleted);
        const collides = !!(name && nameCounts[name.trim().toLowerCase()] > 1);

        // 🔴 WHERE THE ID CAME FROM, and this qualifier is not cosmetic.
        // The server resolves it as COALESCE(provenance.DataSourceID, mapping.DataSourceID). The
        // first arm is a RECORD of the send. The second is the mapping AS IT IS NOW — and
        // dbo.CampaignsToDataSources has PK (CampaignID, Channel) with no history, so re-pointing a
        // campaign is an in-place UPDATE that erases what it used to be.
        //
        // So on a row the SP already graded 'Unverifiable' (the mapping was touched after the send),
        // an unqualified source name is a confident claim about which file was sent — asserted from
        // a mapping that may since have been pointed somewhere else entirely. That is exactly the
        // failure this screen exists to prevent, and it is NOT covered by the provenance badge three
        // cells away: this line reads as an independent statement.
        //
        // The qualifier names the SOURCE OF THE FACT ("according to the mapping"), deliberately NOT
        // a certainty word. The certainty ladder (מתועד/משוחזר/לא ניתן לאימות) stays the exclusive
        // vocabulary of the version cell; adding a fourth term here would make the reader count two
        // findings where there is one.
        const fromRecord = r.ProvenanceSource === 'Recorded';

        // Six states, six different appearances. (ה) and (ו) differ STRUCTURALLY — one has no
        // number, the other has one — so a screenshot can tell them apart.
        let body: React.ReactNode;
        let a11y: string;
        if (id == null) {
            // (ה) no source recorded at all. A record fact, NOT a certainty verdict: the words
            // "לא ניתן לאימות" belong to the version cell and must not be echoed three cells away,
            // or the reader counts two findings where there is one.
            body = <span style={{ fontSize: 12, color: 'var(--pls-d-text-2, #5b6b7b)' }}>{t(`${SS}source.noRecord`)}</span>;
            a11y = t(`${SS}source.a11y.cellNoRecord`);
        } else if (!src || name === null) {
            // (ו) we have an id, the map has no entry for it. Different from (ה): there ARE digits.
            body = (
                <>
                    <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'isolate', fontSize: 11.5, color: 'var(--pls-d-text-2, #5b6b7b)' }}>{`#${id}`}</span>
                    <span style={{ fontSize: 12, color: 'var(--pls-d-text-2, #5b6b7b)' }}>{` · ${t(`${SS}source.nameNotFound`)}`}</span>
                </>
            );
            a11y = t(`${SS}source.a11y.cellNameNotFound`, { id });
        } else {
            // (א)/(ב)/(ג). `<bdi>` is first-strong isolation: a Hebrew name renders RTL, an
            // "AgentPortfolio_Q3" renders LTR, and neither drags the label or the number to the
            // wrong end. Setting direction on the whole row instead would reorder the Hebrew.
            body = (
                <>
                    {/* `0 1 auto`, not `1 1 auto` (CHANGED 2026-08-16): a GROW item absorbs all free
                        space, which pushed the #id to the far edge and made the row full-bleed — so
                        the justifyContent above would have had nothing left to centre. Shrink is
                        kept, so with minWidth:0 + nowrap a long name still ellipsizes. */}
                    <bdi style={{
                        flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap', fontSize: 12, fontWeight: 500, color: 'var(--pls-d-text-2, #5b6b7b)',
                    }}>{name === '' ? t(`${SS}source.nameNotFound`) : name}</bdi>
                    <span dir="ltr" style={{
                        flex: '0 0 auto', direction: 'ltr', unicodeBidi: 'isolate', fontSize: 11.5,
                        color: 'var(--pls-d-text-2, #5b6b7b)', fontVariantNumeric: 'tabular-nums',
                        fontWeight: collides ? 700 : 400,
                    }}>{`#${id}`}</span>
                </>
            );
            a11y = isDeleted
                ? t(fromRecord ? `${SS}source.a11y.cellDeleted` : `${SS}source.a11y.cellDeletedMapped`, { name, id })
                : t(fromRecord ? `${SS}source.a11y.cell` : `${SS}source.a11y.cellMapped`, { name, id });
        }

        return (
            <>
                {/* CHANGED 2026-08-16. justifyContent, NOT textAlign: this is a flex row, and
                    textAlign never positioned its items — the line rendered edge-to-edge while the
                    campaign name above it centred, so the cell had two lines on two different axes.
                    `direction` stays: it decides which end the label and the #id sit at. */}
                <Box aria-hidden style={{
                    display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 6,
                    marginTop: 2, direction: isRtl ? 'rtl' : 'ltr',
                }}>
                    {/* The chip REPLACES the "מקור:" label rather than trailing the name: it then
                        carries the noun, forms a vertical rule of identical rectangles at one x for
                        a 50-row scan, and is never the part the ellipsis eats (`flex:'0 0 auto'` —
                        only the <bdi> shortens).
                        borderRadius 3 + a border is deliberately the OPPOSITE shape language to
                        every other chip on this screen (radius 11, borderless), so the shape itself
                        is a carrier and the signal does not rest on colour — WCAG 1.4.1.
                        NEVER the warn palette (#B54708/#FCF0E6): that pair means "we cannot vouch
                        for which version was sent". Deletion is a certain lifecycle fact, and
                        painting it as a warning raises an alarm on a row that may read "מתועד".
                        QA BUG-19 (ST-05) — (ה) has no source at all, so no label: «אין רישום» alone. A
                        "מקור לפי המיפוי:" in front of it claimed a mapping fact for a row that has none. */}
                    {id == null ? null : isDeleted ? (
                        <span style={{
                            flex: '0 0 auto', fontSize: 11, fontWeight: 800, lineHeight: '15px',
                            color: 'var(--pls-d-text, #3b4754)', background: 'var(--pls-d-surface-2, #EDF0F3)', border: '1px solid #838F9B',
                            borderRadius: 3, padding: '0 5px', whiteSpace: 'nowrap',
                        }}>{t(fromRecord ? `${SS}source.deletedChip` : `${SS}source.deletedChipMapped`)}</span>
                    ) : (
                        <span style={{ flex: '0 0 auto', fontSize: 12, color: 'var(--pls-d-text-2, #5b6b7b)' }}>
                            {t(fromRecord ? `${SS}source.prefix` : `${SS}source.prefixMapped`)}
                        </span>
                    )}
                    {body}
                </Box>
                {/* The visual row is aria-hidden and the whole fact is spoken as one sentence:
                    a screen reader announcing "hash 412" out of a flex row is noise, and
                    "מזהה 412" inside a sentence is the same fact, usable. */}
                <span style={SR_ONLY}>{a11y}</span>
            </>
        );
    };

    // The left/leading stripe encodes WHAT KIND of row this is (`Mock-v3:78-80`):
    //   blue  (--blue)  roll-up / supervisor recipient
    //   amber (--warn)  synthetic — no source row; the record was created by the engagement itself
    //   red   (--risk)  no row in the locked source version at all ⇒ nothing was sent to this person
    // Checked in that order: a supervisor row is a roll-up first and foremost.
    const stripe = (r: SendSearchRow): string | undefined => {
        if (r.IsSupervisor) return '#0371AD';
        if (r.IsSynthetic) return '#B54708';
        if (!r.HasRow) return '#B42318';
        return undefined;
    };

    // The WhatsApp column's grey sub-line, in words. WHICH line it is (error / reason / template) is
    // decided by `waSubLineOf` in the model — this only turns that decision into text.
    const waSubLine = (r: SendSearchRow): { text: string | null; title: string | null } => {
        const s = waSubLineOf(r);
        if (!s) return { text: null, title: null };
        if (s.kind === 'error') {
            return {
                text: s.code != null ? t(`${SS}wa.errorCode`, { code: s.code }) : t(`${SS}wa.errorNoCode`),
                // The provider's own words behind the code, on hover — too long for a grid cell.
                title: r.WaErrorText || null,
            };
        }
        if (s.kind === 'reason') {
            const text = t(`${SS}wa.reason.${s.reason}`);
            // The cell ellipsizes at 220px, so every reason carries its full sentence on hover.
            // 'removed' / 'invalid' come from the number's CURRENT state (column 45), not its state at
            // send time — the hover says so in words, like the «מוסר» / «שגוי» chip's tooltip (QA BUG-22).
            const note = s.reason === 'removed' || s.reason === 'invalid'
                ? ` · ${t(`${SS}wa.reason.currentStateNote`)}`
                : '';
            return { text, title: `${text}${note}` };
        }
        return { text: s.name, title: s.name };
    };

    // The WhatsApp column's campaign name: the unit (`WaUnitName`). On a WhatsApp-only send the
    // server's `CampaignName` IS the unit name (DataSources_SearchSends_Stage: "e-mail campaign name,
    // else the unit name"), so it is the fallback there — and never on a send that has e-mail, where
    // `CampaignName` is the e-mail campaign.
    const waCampaignName = (r: SendSearchRow): string | null =>
        (r.WaUnitName ?? '').trim()
        || (!rowSendHasEmail(r) ? (r.CampaignName ?? '').trim() : '')
        || null;

    const renderRow = (r: SendSearchRow) => {
        const leading = stripe(r);
        // §8: the FIRST name, bold, with the full name on hover. Falls back to the full name for a
        // server that does not project `RecipientFirstName` (or a person whose first name is blank),
        // so the cell is never empty.
        const firstName = (r.RecipientFirstName ?? '').trim() || r.RecipientName;
        const wa = waSubLine(r);
        // 2026-10-02 (Idan): the phone FROM THE SOURCE ROW (column 44), never the client card's.
        const phone = rowRecipientPhoneOf(r);
        // QA BUG-22: the e-mail's chip (column 46).
        const emailBlock = rowRecipientEmailBlockOf(r);
        const lineStyle: React.CSSProperties = {
            color: 'var(--pls-d-text-2, #5b6b7b)', fontSize: 14, direction: 'ltr', unicodeBidi: 'isolate', whiteSpace: 'nowrap',
        };
        return (
            <TableRow
                key={sendSearchRowKey(r)}
                hover
                onClick={() => onOpenRow(r)}
                // Keyboard parity for a clickable row: the mock is mouse-only, but a row that is the
                // ONLY way into the drawer must be reachable without a mouse.
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenRow(r); } }}
                style={{ cursor: 'pointer' }}
            >
                {/* ── נמען ── CHANGED 2026-10-02 (Idan): centred, one fact per line —
                    first name · e-mail · cellphone · the data source the send came from (the source
                    line moved here from the removed שליחה column). */}
                <TableCell
                    align="center"
                    style={{
                        minWidth: 230,
                        boxShadow: leading ? `inset 3px 0 0 ${leading}` : undefined,
                    }}
                >
                    <Box style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Tooltip title={r.RecipientName || ''}>
                            <Typography component="span" style={{ fontWeight: 700 }}>{firstName}</Typography>
                        </Tooltip>
                        {r.IsSupervisor && (
                            <Chip
                                size="small"
                                // `badge.supervisor` ("מפקח"), NOT `kind.rollup` — that key is the
                                // FILTER label ("מפקחים בלבד") and rendering it here put a filter
                                // caption next to a person's name.
                                label={t(`${SS}badge.supervisor`)}
                                style={{ backgroundColor: 'var(--pls-d-surface-2, #E7F1F8)', color: '#0371AD', fontWeight: 800, fontSize: 11.5 }}
                            />
                        )}
                    </Box>
                    {/* E-mail, then cellphone UNDER it, each LTR-isolated (an address or a number must
                        not reorder inside an RTL cell). CHANGED 2026-10-02 (Idan): the cellphone is the
                        SOURCE ROW's (`rowRecipientPhoneOf`, column 44) — the same value the export's
                        mobile column carries — never the client card's; a source row with no phone
                        shows none. The number a WhatsApp actually went to (column 43) is shown in the
                        drawer's WhatsApp card, where it is labelled as such (review round 2). */}
                    {/* QA BUG-22 (Idan): the e-mail gets the phone's shape — the address LTR-isolated, its
                        chip («מוסר» / «שגוי», column 46) OUTSIDE it. */}
                    {!!r.RecipientEmail && (
                        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <Typography component="span" style={lineStyle}>{r.RecipientEmail}</Typography>
                            <ContactBlockChip block={emailBlock} of="email" />
                        </Box>
                    )}
                    {!!phone.phone && (
                        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <Typography component="span" style={lineStyle}>{phone.phone}</Typography>
                            {/* The chip sits OUTSIDE the LTR-isolated number, so its Hebrew word keeps the
                                page direction. QA BUG-22: «מוסר» / «שגוי» (CellBlockReason 'Removed' /
                                'Invalid') is the card's CURRENT state, not its state at send time — the
                                tooltip says so in words (ContactBlockChip). */}
                            <ContactBlockChip block={phone.block} of="phone" />
                        </Box>
                    )}
                    {/* The source line answers what the version cell cannot: "גרסה 3" — of what. */}
                    {renderSourceLine(r)}
                </TableCell>

                {/* ── אימייל ── e-mail campaign name + status + time; `—` when the send has no e-mail
                    campaign (§8). `CampaignName` is the e-mail campaign whenever the send has one. ── */}
                <TableCell align="center">
                    <ChannelStateCell attempt={rowEmailAttempt(r)} name={r.CampaignName} />
                </TableCell>

                {/* ── וואטסאפ ── unit name + status + time + grey sub-line (error / not-sent reason /
                    template) ── */}
                <TableCell align="center">
                    <ChannelStateCell
                        attempt={rowWhatsAppAttempt(r)}
                        name={waCampaignName(r)}
                        subLine={wa.text}
                        subLineTitle={wa.title}
                    />
                </TableCell>

                {/* ── מפקח ── */}
                <TableCell align="center">
                    {r.SupervisorName
                        ? <Typography component="span" style={{ fontSize: 14 }}>{r.SupervisorName}</Typography>
                        : <Typography component="span" style={{ color: '#a8b2bb' }}>—</Typography>}
                </TableCell>

                {/* ── גרסה ── IMMEDIATELY BEFORE נשלח (CONTRACT §4.2). Never blank. ──
                    CHANGED 2026-08-16. A supervisor SUMMARY row has EffectiveVersionID NULL, so
                    VersionState is NULL and the badge forces its warning tone and attaches the tooltip
                    "המיפוי עודכן אחרי השליחה — לא ניתן לקבוע בוודאות איזו גרסה נשלחה": a specific,
                    checkable, UNTRUE reason for a mail that has no mapping and no version concept at
                    all. The honest answer is the word "לא זמין" — never a dash, which would read as
                    "the version is empty", a different claim. */}
                <TableCell align="center">
                    {r.IsSupervisor
                        ? (
                            <Typography component="span" style={{ fontSize: 13.5, color: 'var(--pls-d-text-2, #5b6b7b)' }}>
                                {t(`${SS}supervisor.notAvailable`)}
                            </Typography>
                        )
                        : (
                            <VersionBadge
                                VersionNumber={r.VersionNumber}
                                ProvenanceSource={r.ProvenanceSource}
                                VersionState={r.VersionState}
                            />
                        )}
                </TableCell>

                {/* ── נשלח ── */}
                <TableCell align="center" style={{ direction: 'ltr', fontSize: 13.5 }}>
                    {fmt(r.SentAt)}
                </TableCell>

                {/* ── ערך המיון ── present ONLY while a sort field is chosen ──
                    `direction:'ltr'` because the values are ids, policy numbers, amounts and dates —
                    RTL reorders their digits and the operator would read a different number than the
                    one the server sorted on. An ABSENT value says "אין ערך" in words rather than
                    showing a dash: a row with no value for the sort field is sorted into a group
                    (§2 `SortIsUnknown`), and a dash would read as "the value is empty", which is a
                    different fact. */}
                {!!sortFieldLabel && (
                    <TableCell align="center" style={{ direction: 'ltr', fontSize: 13.5 }}>
                        {sortValueDisplayOf(r) ?? (
                            <Typography component="span" style={{ fontSize: 12.5, color: '#a8b2bb' }}>
                                {t(`${SS}sort.unknownValue`)}
                            </Typography>
                        )}
                    </TableCell>
                )}

                {/* ── action ── the row is clickable; this is the affordance, not a second path.
                    stopPropagation is deliberate: without it the row handler fires too and the
                    drawer would be pushed twice, which under the depth cap silently swallows the
                    second push and looks like a dead button. */}
                <TableCell align="center">
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => { e.stopPropagation(); onOpenRow(r); }}
                    >
                        {t(`${SS}${r.IsSupervisor ? 'action.viewRollup' : 'action.view'}`)}
                    </Button>
                </TableCell>
            </TableRow>
        );
    };

    const renderHead = () => (
        <TableHead>
            <TableRow>
                {/* CHANGED 2026-10-02 (Idan): every header is centred, matching its body cell —
                    נמען included. The שליחה column is gone (see the file header). */}
                <TableCell align="center">{t(`${SS}col.recipient`)}</TableCell>
                <TableCell align="center">{t(`${SS}col.email`)}</TableCell>
                <TableCell align="center">{t(`${SS}col.whatsapp`)}</TableCell>
                <TableCell align="center">{t(`${SS}col.supervisor`)}</TableCell>
                <TableCell align="center">{t(`${SS}version.label`)}</TableCell>
                <TableCell align="center">{t(`${SS}col.sent`)}</TableCell>
                {/* The header NAMES the field being sorted on ("ערך המיון · מספר פוליסה"). A generic
                    "ערך המיון" would leave the user to guess which of their columns produced it. */}
                {!!sortFieldLabel && (
                    <TableCell align="center">
                        {`${t(`${SS}col.sortValue`)} · ${sortFieldLabel}`}
                    </TableCell>
                )}
                <TableCell align="center" />
            </TableRow>
        </TableHead>
    );

    // An empty grid must say WHICH empty it is. With filters on, the answer is "no match" and the
    // way out is offered; the loading case is handled by the caller's Loader, so this never flashes
    // during a fetch.
    // 11-sendsearch §8: THREE empties, never one. A failed load is an ERROR state (the slice has
    // already cleared the rows, so without this branch it rendered as "no results" — a claim about
    // the data from a request that never answered). Under a channel filter the sentence names the
    // channel and offers "show all channels", because the person may well be there on the other one.
    const renderEmpty = () => {
        if (error) {
            const denied = error === 'PERMISSION_DENIED';
            return (
                <Box style={{ padding: '24px 0' }}>
                    <InlineBanner
                        severity={denied ? 'warning' : 'error'}
                        role="alert"
                        title={t(denied ? `${SS}error.permissionDenied` : `${SS}error.loadFailed`)}
                    />
                </Box>
            );
        }
        const narrowed = channel != null && channel !== eSendSearchChannel.ALL;
        return (
            <Box style={{ textAlign: 'center', padding: '48px 16px', color: 'var(--pls-d-text-2, #5b6b7b)' }}>
                <Typography style={{ fontSize: 18, fontWeight: 600 }}>
                    {narrowed
                        ? t(`${SS}empty.noResultsChannel`, { channel: t(channelFilterKey(channel)) })
                        : t(`${SS}empty.noResults`)}
                </Typography>
                <Box style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginTop: 12 }}>
                    {narrowed && onShowAllChannels && (
                        <Button variant="outlined" color="primary" onClick={onShowAllChannels}>
                            {t(`${SS}empty.showAllChannels`)}
                        </Button>
                    )}
                    {hasFilter && (
                        <Button onClick={onClearAll}>{t(`${SS}clearAll`)}</Button>
                    )}
                </Box>
            </Box>
        );
    };

    if (!loading && (!!error || items.length === 0)) return renderEmpty();

    return (
        <>
            <TableContainer style={{ background: 'var(--pls-d-surface, #fff)', border: '1px solid var(--pls-d-border, #e0e0e0)', borderRadius: 10 }}>
                <Table style={{ minWidth: 1120 }}>
                    {renderHead()}
                    <TableBody>{items.map(renderRow)}</TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={totalCount}
                page={pageIndex}
                onPageChange={(_: any, page: number) => onPageChange(page)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e: any) => onPageSizeChange(parseInt(e.target.value, 10))}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            />
        </>
    );
};

export default SendSearchTable;
